import { useState, useEffect, useRef } from "react";
import Icon from "../components/Icon";
import { BtnGhost } from "../components/Atoms";
import { useHelpdesk } from "../hooks/useHelpdesk";
import DetalleTicketModal from "../components/modals/DetalleTicketModal";
import SelectorUsuario from "../components/widgets/SelectorUsuario";
import DatePicker from "../components/forms/DatePicker";
import SelectorEtapa from "../components/widgets/SelectorEtapa";
import { mapaColoresEtapas, colorEtapa as colorPorNombre, etapaES } from "../utils";
import { HD_PRIOS } from "../constants";
import { clicable } from "../components/ClicableTeclado.jsx";

// Seguimiento de incidencias del Servicio de asistencia de Odoo
// (módulo helpdesk_mgmt de OCA).

// Las etiquetas de prioridad viven en constants.js (HD_PRIOS): el selector de
// incidencias del cronómetro las pinta igual y dos copias acabarían divergiendo.
const PRIORIDADES = HD_PRIOS;


const fecha = iso => iso
  ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit",
                                            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" })
  : "—";


const PAGINA = 40;

export default function HelpdeskPage({ token, showToast, onConnect, ticketInicial, onTicketInicialVisto,
                                       senalNuevos = 0, crono, agendaTiposData = [], onIrAInicio }) {
  const h = useHelpdesk(token, showToast);
  const [vista,   setVista]   = useState("kanban");   // 'kanban' | 'lista'
  const [filtros, setFiltros] = useState({ team: "", stage: "", mine: "", q: "",
                                          desde: "", hasta: "", incluir_cerradas: "" });
  const [offset,  setOffset]  = useState(0);
  const [abierto, setAbierto] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);

  // El alto del tablero se mide en vez de restar un numero fijo a 100vh: con un
  // calc a ojo, las columnas cortaban antes de tiempo y dejaban hueco vacio
  // debajo, y cualquier cambio en los filtros lo descuadraba.
  const tablero = useRef(null);
  const [altoTablero, setAltoTablero] = useState(0);
  const [mensajes, setMensajes] = useState([]);

  useEffect(() => { h.loadMeta(); h.loadUsuarios(); }, [h.loadMeta, h.loadUsuarios]);

  // Cada consulta es una transacción del ORM de Odoo: se espera a que el
  // usuario deje de teclear en vez de lanzar una por pulsación.
  useEffect(() => {
    const id = setTimeout(() => {
      setOffset(0);
      if (vista === "kanban") h.loadKanban(filtros);
      else h.loadTickets({ ...filtros, limit: PAGINA, offset: 0 });
    }, 350);
    return () => clearTimeout(id);
  }, [filtros, vista]); // eslint-disable-line

  useEffect(() => {
    if (vista === "lista" && offset > 0) h.loadTickets({ ...filtros, limit: PAGINA, offset });
  }, [offset]); // eslint-disable-line

  // Al entrar un aviso de incidencia nueva se recarga la vista que este puesta,
  // con los filtros y la pagina tal cual: el aviso y el tablero contarian cosas
  // distintas hasta que alguien tocase un filtro. La primera senal es el sondeo
  // inicial, que ya trae la lista recien pedida, asi que no se recarga.
  const primeraSenal = useRef(true);
  useEffect(() => {
    if (!senalNuevos) return;
    if (primeraSenal.current) { primeraSenal.current = false; return; }
    if (vista === "kanban") h.loadKanban(filtros);
    else h.loadTickets({ ...filtros, limit: PAGINA, offset });
  }, [senalNuevos]); // eslint-disable-line

  // Al pulsar el aviso de incidencia nueva se llega aquí con el id: se abre el
  // detalle directamente en vez de dejar al usuario buscándolo en el tablero.
  useEffect(() => {
    if (!ticketInicial) return;
    (async () => {
      setCargandoDetalle(true);
      const [full, adj, msg] = await Promise.all([
        h.loadTicket(ticketInicial), h.loadAdjuntos(ticketInicial), h.loadMensajes(ticketInicial)]);
      setCargandoDetalle(false);
      if (full) { setAbierto(full); setAdjuntos(adj || []); setMensajes(msg || []); }
      onTicketInicialVisto && onTicketInicialVisto();
    })();
  }, [ticketInicial]); // eslint-disable-line

  useEffect(() => {
    const medir = () => {
      const el = tablero.current;
      if (!el) return;
      setAltoTablero(Math.max(240, window.innerHeight - el.getBoundingClientRect().top - 16));
    };
    medir();
    window.addEventListener("resize", medir);
    // Los filtros se envuelven en varias lineas segun el ancho: al cambiar de
    // alto mueven el tablero hacia abajo.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
    if (ro && tablero.current?.parentElement) ro.observe(tablero.current.parentElement);
    return () => { window.removeEventListener("resize", medir); if (ro) ro.disconnect(); };
  }, [vista, h.kanban.etapas.length]);

  const abrir = async (t) => {
    setAbierto({ ...t, descripcion: null });
    setAdjuntos([]); setMensajes([]);
    setCargandoDetalle(true);
    const [full, adj, msg] = await Promise.all([
      h.loadTicket(t.id), h.loadAdjuntos(t.id), h.loadMensajes(t.id)]);
    setCargandoDetalle(false);
    if (full) setAbierto(full);
    setAdjuntos(adj || []);
    setMensajes(msg || []);
  };

  // Etapas de cierre, buscadas por el nombre y no por id: cada instalacion de
  // helpdesk_mgmt numera las suyas. Si una no existe, su boton no se pinta.
  const etapaPorPatron = re => h.meta.etapas.find(e => re.test(e.name || ""));
  const etResuelta  = etapaPorPatron(/done|resuel|soluci/i);
  const etCancelada = etapaPorPatron(/cancel|descart/i);

  // Cambiar la etapa desde la tarjeta mueve la incidencia de columna y cambia
  // los totales, asi que despues se recarga la vista: el estado local sabe
  // mover el ticket, pero no recontar la columna de la que sale.
  // Devuelve la incidencia actualizada, o null si Odoo lo rechazo: quien llama
  // necesita saberlo para no confirmar un cambio que no ha ocurrido.
  const cambiarTicket = async (id, cambios, recargar) => {
    const t = await h.actualizar(id, cambios);
    if (!t) return null;
    if (abierto?.id === id) setAbierto(a => ({ ...a, ...t }));
    if (recargar) {
      if (vista === "kanban") h.loadKanban(filtros);
      else h.loadTickets({ ...filtros, limit: PAGINA, offset });
    }
    return t;
  };

  // Resolver y cancelar son el 90 % de lo que se hace con una incidencia: van
  // en la propia tarjeta para no tener que abrir la ficha.
  const AccionesCierre = ({ t, size = 24 }) => (
    <div style={{ display:"flex", gap:4 }} onClick={e => e.stopPropagation()}>
      {/* Resolver lleva el color; Cancelar va neutro y mas discreto, porque esta a un
          clic de Resolver y es la accion que no se quiere pulsar por error. */}
      {[[etResuelta, "check2", "var(--ok-text)", "var(--ok-line)", "var(--ok-bg)", "Resolver", "Marcar como resuelta"],
        [etCancelada, "x-lg", "var(--text3)", "var(--border)", "transparent", "Cancelar", "Cancelar la incidencia"]]
        .filter(([et]) => et && String(et.id) !== String(t.etapa?.id))
        .map(([et, icono, color, linea, fondo, etiqueta, texto]) => (
          <button key={et.id} className="btn" title={texto} aria-label={texto}
            onClick={() => cambiarTicket(t.id, { stage_id: et.id }, true)}
            style={{ minHeight:size, padding:"0 8px", display:"flex", alignItems:"center",
                     gap:4, borderRadius:7, cursor:"pointer", fontSize:11, fontWeight:600,
                     fontFamily:"var(--font)", color,
                     border:`1px solid ${linea}`, background:fondo }}>
            <Icon name={icono} size={11} color={color}/>{etiqueta}
          </button>
        ))}
    </div>
  );

  const card  = { background:"var(--surface)", borderRadius:13, border:"1px solid var(--border)", boxShadow:"var(--shadow-sm)" };
  const input = { padding:"7px 11px", borderRadius:8, border:"1px solid var(--border)", fontSize:12,
                  fontFamily:"var(--font)", background:"var(--surface)", color:"var(--text2)" };
  const chip  = activo => ({ ...input, cursor:"pointer", fontWeight:600,
                             background: activo ? "var(--accent-bg)" : "var(--surface)",
                             color: activo ? "var(--accent)" : "var(--text2)" });

  if (h.sinConexion) {
    return (
      <div className="fu" style={{ ...card, padding:28, textAlign:"center", maxWidth:460, margin:"40px auto" }}>
        <Icon name="headset" size={30} color="var(--text3)" style={{ display:"block", margin:"0 auto 12px" }}/>
        <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>Conecta tu cuenta de Odoo</div>
        <div style={{ fontSize:12.5, color:"var(--text3)", lineHeight:1.55, marginBottom:16 }}>
          Las incidencias se leen de Odoo con tu propio usuario, así que ves exactamente
          lo mismo que verías allí.
        </div>
        <BtnGhost onClick={() => onConnect && onConnect()}>Ir al panel para conectar</BtnGhost>
      </div>
    );
  }

  // El color se resuelve sobre la lista COMPLETA de etapas: es lo que permite
  // garantizar que dos etapas no acaben del mismo color (En espera y Cola de
  // tareas caían las dos en el color por defecto).
  const colores = mapaColoresEtapas(h.meta.etapas);
  const colorDe = et => (et && colores[et.id]) || colorPorNombre(et?.nombre) || "#8b5cf6";

  const cerradasVisibles = filtros.incluir_cerradas === "1";

  // Una sola columna de tarjetas por etapa, con scroll vertical interno. Antes la
  // que mas acumulaba pasaba a dos sub-columnas, y la segunda quedaba cortada por
  // el borde del tablero (T-078): nada de recortar tarjetas a ningun ancho.
  const ANCHO_COL = 288;

  return (
    <div className="fu" style={{ display:"flex", flexDirection:"column", minHeight:0, flex:1 }}>
      {/* Filtros */}
      <div className="filters-bar bar-wrap" style={{ display:"flex", gap:8, alignItems:"center", marginBottom:14 }}>
        <div style={{ position:"relative", flex:"1 1 200px", minWidth:170 }}>
          <Icon name="search" size={12} color="var(--text3)"
            style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)" }}/>
          <input aria-label="Buscar incidencia por título o número" value={filtros.q} onChange={e => setFiltros(f => ({ ...f, q: e.target.value }))}
            placeholder="Buscar por título o número…"
            style={{ ...input, width:"100%", paddingLeft:28, boxSizing:"border-box" }}/>
        </div>

        <select aria-label="Equipo" value={filtros.team} onChange={e => setFiltros(f => ({ ...f, team: e.target.value }))} style={input}>
          <option value="">Todos los equipos</option>
          {h.meta.equipos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        <SelectorEtapa etapas={h.meta.etapas} valor={filtros.stage} todas="Todos los estados" ancho={172}
          onChange={id => setFiltros(f => ({ ...f, stage: id }))}/>

        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
          {/* Los dos ultimos <input type="date"> nativos de la app. El nativo
              pinta "dd/mm/aaaa" y despliega el calendario del navegador
              —distinto en cada navegador y sistema, en su idioma y con la
              semana empezando en domingo—, y el colorScheme que habia aqui al
              lado estaba solo para que ese calendario ajeno no saliera en
              blanco sobre el tema oscuro. Con el DatePicker de la casa ese
              problema no existe.
              El acotado entre ambos se mantiene: max en el "desde" y min en el
              "hasta", que es lo que impide pedir un rango del reves. */}
          <DatePicker value={filtros.desde} max={filtros.hasta} compact
            nombre="Creadas desde" placeholder="Creadas desde" style={{ width: 136 }}
            onChange={v => setFiltros(f => ({ ...f, desde: v }))}/>
          <span style={{ fontSize:11, color:"var(--text3)" }}>→</span>
          <DatePicker value={filtros.hasta} min={filtros.desde} refDate={filtros.desde} compact
            nombre="Creadas hasta" placeholder="Creadas hasta" style={{ width: 136 }}
            onChange={v => setFiltros(f => ({ ...f, hasta: v }))}/>
          {(filtros.desde || filtros.hasta) && (
            <button aria-label="Quitar el filtro de fechas" className="btn" title="Quitar el filtro de fechas"
              onClick={() => setFiltros(f => ({ ...f, desde: "", hasta: "" }))}
              style={{ padding:5, border:"none", background:"transparent", cursor:"pointer", lineHeight:0 }}>
              <Icon name="x" size={13} color="var(--text3)"/>
            </button>
          )}
        </div>

        <button className="btn" style={chip(filtros.mine === "1")}
          onClick={() => setFiltros(f => ({ ...f, mine: f.mine === "1" ? "" : "1" }))}>
          Asignadas a mí
        </button>

        <button className="btn" style={chip(cerradasVisibles)}
          onClick={() => setFiltros(f => ({ ...f, incluir_cerradas: cerradasVisibles ? "" : "1" }))}
          title="Resueltas y canceladas están ocultas por defecto">
          <Icon name={cerradasVisibles ? "eye" : "eye-slash"} size={12}
            color={cerradasVisibles ? "var(--accent)" : "var(--text3)"}/>
          <span style={{ marginLeft:5 }}>Cerradas</span>
        </button>

        <div data-bar-end style={{ display:"flex", gap:0, marginLeft:"auto", border:"1px solid var(--border)", borderRadius:8, overflow:"hidden" }}>
          {[["kanban","kanban-fill"],["lista","list-ul"]].map(([v, ic]) => (
            <button aria-label={v === "kanban" ? "Vista kanban" : "Vista lista"} key={v} className="btn" onClick={() => setVista(v)}
              title={v === "kanban" ? "Vista kanban" : "Vista lista"}
              style={{ padding:"7px 11px", border:"none", cursor:"pointer",
                       background: vista === v ? "var(--accent-bg)" : "var(--surface)" }}>
              <Icon name={ic} size={13} color={vista === v ? "var(--accent)" : "var(--text3)"}/>
            </button>
          ))}
        </div>

        <span style={{ fontSize:11.5, color:"var(--text3)" }}>
          {h.cargando ? "Consultando Odoo…"
            : `${vista === "kanban" ? h.kanban.total : h.total} incidencia${(vista === "kanban" ? h.kanban.total : h.total) === 1 ? "" : "s"}`}
        </span>
      </div>

      {/* ── Kanban ── */}
      {vista === "kanban" && (
        <>
          <div ref={tablero}
            style={{ display:"flex", gap:12, overflowX:"auto", alignItems:"flex-start",
                     paddingBottom:10, height: altoTablero || undefined }}>
            {h.kanban.etapas.map(et => {
              const col   = colorDe(et);
              const suyos = h.kanban.tickets.filter(t => t.etapa?.id === et.id);
              const tot   = h.kanban.totales[et.id] || 0;
              const ancho = ANCHO_COL;
              return (
                // En movil una columna de 288px fijos deja fuera la siguiente:
                // se acota al ancho de pantalla para que se intuya el arrastre.
                <div key={et.id} style={{ flex:"0 0 auto", width:`min(${ancho}px, 84vw)`, maxWidth:ancho,
                                          display:"flex", flexDirection:"column", gap:8 }}>
                  {/* La cabecera lleva el color de la etapa de fondo: con solo el
                      punto habia que leer el rotulo para saber en que columna estabas. */}
                  <div style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 9px",
                                borderRadius:9, background:`${col}14`, border:`1px solid color-mix(in srgb, ${col} 45%, transparent)` }}>
                    <span style={{ fontSize:11.5, fontWeight:800, textTransform:"uppercase",
                                   letterSpacing:".06em", color:col }}>{etapaES(et.nombre)}</span>
                    <span style={{ marginLeft:"auto", fontSize:10.5, fontWeight:800, padding:"2px 8px",
                                   borderRadius:20, background:col, color:"#fff" }}>{tot}</span>
                  </div>

                  <div style={{ display:"flex", flexDirection:"column",
                                gap:7, overflowY:"auto",
                                maxHeight: altoTablero ? altoTablero - 44 : "max(220px, calc(100vh - 265px))" }}>
                    {suyos.map(t => {
                      const prio = PRIORIDADES[t.prioridad] || PRIORIDADES["0"];
                      return (
                        <div key={t.id} onClick={() => abrir(t)} tabIndex={0}
                          onKeyDown={e => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); abrir(t); } }}
                          // El realce va por sombra, no por borderColor, para no
                          // pisar el borde de color de la etapa.
                          style={{ ...card, padding:"10px 11px", cursor:"pointer",
                                   transition:"box-shadow .15s" }}
                          onMouseEnter={e => e.currentTarget.style.boxShadow = "var(--shadow-md)"}
                          onMouseLeave={e => e.currentTarget.style.boxShadow = "var(--shadow-sm)"}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
                            <span style={{ fontSize:10, fontFamily:"var(--mono)", color:"var(--text3)" }}>{t.numero}</span>
                            {t.prioridad !== "0" && (
                              <span style={{ fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:20,
                                             background:`${prio.color}2e`, color:prio.color, marginLeft:"auto",
                                             border:`1px solid ${prio.color}66` }}>
                                {prio.label.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize:12.5, fontWeight:600, lineHeight:1.35, color:"var(--text)",
                                        display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                            {t.titulo}
                          </div>
                          <div style={{ fontSize:10, color:"var(--text3)", marginTop:6, display:"flex", gap:6, flexWrap:"wrap" }}>
                            {t.contacto && <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:130 }}>{t.contacto.nombre}</span>}
                            <span style={{ marginLeft:"auto" }}>{fecha(t.creado)}</span>
                          </div>

                          {/* Asignar y cerrar, sin abrir la ficha */}
                          <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:6, marginTop:8,
                                        paddingTop:7, borderTop:"1px solid var(--border2)" }}>
                            <div onClick={e => e.stopPropagation()} style={{ minWidth:0, maxWidth:"100%", flex:"1 1 100%" }}>
                              <SelectorUsuario usuarios={h.usuarios} compacto
                                valor={t.asignado?.id} nombre={t.asignado?.nombre}
                                disabled={!h.usuarios.length}
                                onChange={id => cambiarTicket(t.id, { user_id: id })}/>
                            </div>
                            <div style={{ marginLeft:"auto", flexShrink:0 }}><AccionesCierre t={t}/></div>
                          </div>
                        </div>
                      );
                    })}

                    {suyos.length === 0 && (
                      <div style={{ ...card, padding:"14px 10px", textAlign:"center", fontSize:11, color:"var(--text3)" }}>
                        Sin incidencias
                      </div>
                    )}
                    {tot > suyos.length && (
                      <div style={{ fontSize:10.5, color:"var(--text3)", textAlign:"center", padding:"6px 0" }}>
                        y {tot - suyos.length} más · usa la vista lista para verlas
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {h.kanban.etapas.length === 0 && !h.cargando && (
              <div style={{ ...card, padding:30, textAlign:"center", color:"var(--text3)", fontSize:12.5, width:"100%" }}>
                No hay etapas que mostrar.
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Lista ── */}
      {vista === "lista" && (
        <>
          <div style={{ ...card, overflow:"hidden" }}>
            {h.tickets.length === 0 && !h.cargando && (
              <div style={{ padding:34, textAlign:"center", color:"var(--text3)", fontSize:12.5 }}>
                No hay incidencias que coincidan con el filtro.
              </div>
            )}
            {h.tickets.map((t, i) => {
              const col  = colorDe(t.etapa);
              const prio = PRIORIDADES[t.prioridad] || PRIORIDADES["0"];
              return (
                <div key={t.id} {...clicable(() => abrir(t), { role: null })}
                  style={{ display:"flex", alignItems:"center", gap:11, padding:"11px 14px", cursor:"pointer",
                           borderTop: i === 0 ? "none" : "1px solid var(--border2)" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--bg)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <span style={{ fontSize:11, fontFamily:"var(--mono)", color:"var(--text3)", width:62, flexShrink:0 }}>{t.numero}</span>
                  <span style={{ width:7, height:7, borderRadius:"50%", background:col, flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"var(--text)", overflow:"hidden",
                                  textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.titulo}</div>
                    <div style={{ fontSize:10.5, color:"var(--text3)", marginTop:2, display:"flex", gap:8, flexWrap:"wrap" }}>
                      {t.contacto && <span>{t.contacto.nombre}</span>}
                      {t.equipo   && <span>· {t.equipo.nombre}</span>}
                      <span>· {fecha(t.creado)}</span>
                    </div>
                  </div>
                  {t.prioridad !== "0" && (
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20,
                                   background:`${prio.color}1f`, color:prio.color, flexShrink:0 }}>
                      {prio.label.toUpperCase()}
                    </span>
                  )}
                  <span className="m-hide" style={{ fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:20,
                                 background:`${col}1f`, color:col, flexShrink:0 }}>{etapaES(t.etapa?.nombre)}</span>
                  <div className="m-hide" onClick={e => e.stopPropagation()} style={{ flexShrink:0 }}>
                    <SelectorUsuario usuarios={h.usuarios} compacto
                      valor={t.asignado?.id} nombre={t.asignado?.nombre}
                      disabled={!h.usuarios.length}
                      onChange={id => cambiarTicket(t.id, { user_id: id })}/>
                  </div>
                  <AccionesCierre t={t} size={26}/>
                </div>
              );
            })}
          </div>

          {h.total > PAGINA && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginTop:14 }}>
              <BtnGhost onClick={() => setOffset(o => Math.max(0, o - PAGINA))}>
                <Icon name="chevron-left" size={11}/> Anterior
              </BtnGhost>
              <span style={{ fontSize:11.5, color:"var(--text3)" }}>
                {offset + 1}–{Math.min(offset + PAGINA, h.total)} de {h.total}
              </span>
              <BtnGhost onClick={() => setOffset(o => (o + PAGINA < h.total ? o + PAGINA : o))}>
                Siguiente <Icon name="chevron-right" size={11}/>
              </BtnGhost>
            </div>
          )}
        </>
      )}

      {abierto && (
        <DetalleTicketModal ticket={abierto} adjuntos={adjuntos} mensajes={mensajes} cargando={cargandoDetalle}
          crono={crono} agendaTiposData={agendaTiposData}
          // «Cambiar de tarea» pausa y lleva al panel de inicio: el selector de
          // tareas vive alli y repetirlo aqui serian dos copias del mismo.
          onIrAInicio={onIrAInicio}
          onResponder={async (cuerpo, interno) => {
            const ok = await h.responder(abierto.id, cuerpo, interno);
            if (ok) setMensajes(await h.loadMensajes(abierto.id));
            return ok;
          }}
          usuarios={h.usuarios} etapas={h.meta.etapas}
          // Cambiar la etapa desde la ficha tambien mueve la tarjeta y recuenta
          // la columna, asi que se recarga la vista igual que desde el tablero.
          onActualizar={cambios => cambiarTicket(abierto.id, cambios, !!cambios.stage_id)}
          onClose={() => setAbierto(null)} showToast={showToast}/>
      )}
    </div>
  );
}

